<template>
    <section class="bg-white dark:bg-gray-900">
      <div class="py-8 px-4 mx-auto max-w-screen-xl lg:py-16">
        <h2 class="mb-4 text-xl font-bold text-gray-900 dark:text-white text-center">Solicitud de citas</h2>
  
        <!-- Filtro Paciente -->
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
          <div>
            <label class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Filtrar por Paciente</label>
            <input v-model="filtroPaciente" type="text"
              class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
              placeholder="Nombre del paciente" />
          </div>
        </div>
  
        <!-- Tabla -->
        <div class="mt-8 flex flex-col justify-center items-center">
          <div class="relative overflow-x-auto shadow-md sm:rounded-lg w-full">
            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th class="px-6 py-3">Paciente</th>
                  <th class="px-6 py-3">Fecha</th>
                  <th class="px-6 py-3">Hora</th>
                  <th class="px-6 py-3">Motivo</th>
                  <th class="px-6 py-3">Estado</th>
                  <th class="px-6 py-3">Opciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cita in citasFiltradas" :key="cita.id"
                  class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td class="px-6 py-4 font-medium text-gray-900 dark:text-white">
                    {{ cita.paciente.nombre }} {{ cita.paciente.apellido }}
                  </td>
                  <td class="px-6 py-4">{{ formatearFecha(cita.fecha) }}</td>
                  <td class="px-6 py-4">{{ cita.hora }}</td>
                  <td class="px-6 py-4">{{ cita.motivo }}</td>
                  <td class="px-6 py-4 text-yellow-500">{{ cita.estado }}</td>
                  <td class="px-6 py-4">
                    <button @click="abrirModal(cita)" class="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded">
                      Editar
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
  
        <!-- Modal -->
        <div v-if="modalAbierto" class="modal overflow-y-auto overflow-x-hidden bg-black bg-opacity-50 fixed inset-0 flex justify-center items-start z-50 pt-20">
          <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
            <h3 class="text-lg font-bold mb-4">Editar Cita</h3>
  
            <!-- Fecha -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-900 mb-2">Fecha:</label>
              <input v-model="citaSeleccionada.fecha" type="date"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
  
            <!-- Hora -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-900 mb-2">Hora:</label>
              <input v-model="citaSeleccionada.hora" type="time"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
  
            <!-- Enfermero -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-900 mb-2">Enfermero:</label>
              <select v-model="enfermeroSeleccionado"
                class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="">Seleccione un enfermero</option>
                <option v-for="enf in enfermeros" :key="enf.id" :value="enf">{{ enf.nombre }}</option>
              </select>
            </div>
  
            <!-- Botones -->
            <div class="flex justify-end space-x-2">
              <button @click="cerrarModal" class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded">
                Cancelar
              </button>
              <button @click="guardarCambios" class="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded">
                Guardar
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
<script>
import { collection, onSnapshot, updateDoc, getDocs, doc, getDoc} from "firebase/firestore";
import { db } from '../../firebase';
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatIsoPrDateKey } from "@/utils/prTime";

export default {
  name: 'solicitud-citas',
  data() {
    return {
      citas: [],
      filtroPaciente: '',
      modalAbierto: false,
      citaSeleccionada: null,
      enfermeros: [],
      enfermeroSeleccionado: {},
    };
  },
  computed: {
    citasFiltradas() {
      return this.citas
        .filter(cita => cita.estado === 'pendiente')
        .filter(cita => {
          const nombreCompleto = `${cita.paciente.nombre} ${cita.paciente.apellido}`.toLowerCase();
          return nombreCompleto.includes(this.filtroPaciente.toLowerCase());
        });
    },
  },
  methods: {
    async cargarEnfermeros() {
      const querySnapshot = await getDocs(collection(db, "enfermeros"));
      this.enfermeros = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    },
    cargarCitas() {
      onSnapshot(collection(db, "citasGlobales"), snapshot => {
        if (!snapshot?.docs) return;
        this.citas = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            fecha: data.fecha,
            estado: data.estado
          };
        });
      });
    },
    abrirModal(cita) {
      this.cargarEnfermeros();
      this.citaSeleccionada = { ...cita };
      this.modalAbierto = true;
    },
    cerrarModal() {
      this.modalAbierto = false;
      this.citaSeleccionada = null;
      this.enfermeroSeleccionado = {};
    },
    async guardarCambios() {
  try {
    if (!this.citaSeleccionada?.id) {
      alert('No se ha seleccionado una cita válida para actualizar.');
      return;
    }

    const docRef = doc(db, "citasGlobales", this.citaSeleccionada.id);
const docSnapshot = await getDoc(docRef);

if (!docSnapshot.exists()) {
  alert('La cita seleccionada no existe en la base de datos.');
  return;
}

// 🔄 Guardar cambios y confirmar cita
await updateDoc(docRef, {
  enfermero: this.enfermeroSeleccionado?.id || null,
  fecha: this.citaSeleccionada.fecha,
  hora: this.citaSeleccionada.hora,
  estado: "confirmada",
  tratamientoCompletado: false 
});

    const functions = getFunctions();
if (this.citaSeleccionada.paymentIntentId) {
  const confirmarPago = httpsCallable(functions, 'confirmarPagoCita');
  await confirmarPago({
    intentId: this.citaSeleccionada.paymentIntentId,
    citaId: this.citaSeleccionada.id

  });
}

    // 📲 Enviar SMS
    const sendSms = httpsCallable(functions, 'sendSms');
    let numero = this.citaSeleccionada.paciente.telefono.toString();
    if (!numero.startsWith('+1')) {
      numero = '+1' + numero;
    }

    const fechaFormateada = this.formatearFecha(this.citaSeleccionada.fecha);
    const hora = this.citaSeleccionada.hora;

    await sendSms({
      to: numero,
      message: `✅ Su cita ha sido confirmada para el ${fechaFormateada} a las ${hora}.\n\nNat Spa\nOasis De Paz y Relajacion`
    });

    alert("✅ Cita confirmada y mensaje enviado");
    this.cargarCitas();
    this.cerrarModal();

  } catch (error) {
    console.error('❌ Error al actualizar la cita:', error);
    alert('Error al guardar los cambios.');
  }
},

formatearFecha(fecha) {
  const fechaKey = formatIsoPrDateKey(fecha);
  if (!fechaKey) return "";
  const base = new Date(`${fechaKey}T12:00:00Z`);
  return base.toLocaleDateString("es-PR", {
    timeZone: "America/Puerto_Rico",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

  },
  mounted() {
    this.cargarCitas();
  },
};
</script>

